/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Page from "components/common_components/Page";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import Status from "components/common_components/Status";
import LoadingPage from "components/common_components/LoadingPage";
import { Form } from "components/common_components/Formik";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import CampaignsContext from "./CampaignsContext";
import CampaignBasicFields from "./components/CampaignBasicFields";
import AssetsSection from "./components/AssetsSection";
import { editCampaignSchema } from "./validation";
import { buildCampaignPayload, mapClonedAssets, getStorageFileNameFromUrl } from "./utils";

function EditCampaign() {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { Campaigns, Storage, cloneState, setCloneState } = useContext(CampaignsContext);

    const [isLoading, setIsLoading] = useState(false);
    const [campaign, setCampaign] = useState(undefined);
    // Original assets to preload/clone when this edit is the target of a clone.
    const [cloneAssets, setCloneAssets] = useState(null);

    useEffect(() => {
        if (!id) {
            dispatch(notifyError("No ID included in the path."));
            history.push("/campaigns");
            return;
        }
        setIsLoading(true);
        Campaigns.getOneById({
            id,
            onSuccess: (data) => {
                setCampaign(data);
                if (cloneState && String(cloneState.newCampaignId) === String(id)) {
                    setCloneAssets(mapClonedAssets(cloneState.assets));
                }
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                setIsLoading(false);
                history.push("/campaigns");
            },
        });
        return () => setCampaign(undefined);
    }, [id]);

    const isCloneTarget = Boolean(cloneAssets);

    const initialValues = campaign
        ? {
              name: campaign.name || "",
              description: campaign.description || "",
              startDatetime: campaign.startDatetime || null,
              endDatetime: campaign.endDatetime || null,
              assets:
                  cloneAssets ||
                  (campaign.assets || []).map((asset) => ({
                      id: asset.id || null,
                      text1: asset.text1 || "",
                      text2: asset.text2 || "",
                      text3: asset.text3 || "",
                      imageUrl: asset.imageUrl || "",
                      onImageClickUrl: asset.onImageClickUrl || "",
                  })),
          }
        : { name: "", description: "", startDatetime: null, endDatetime: null, assets: [] };

    const onCancel = () => {
        setCloneState(null);
        history.push(`/campaigns/${id}`);
    };

    // Copies any asset image still pointing to the cloned source into this
    // campaign's storage folder, returning the assets with their final URLs.
    const cloneAssetImages = async (assets = []) => {
        const result = [];
        for (let index = 0; index < assets.length; index += 1) {
            const asset = assets[index];
            let { imageUrl } = asset;
            const needsClone = asset.sourceImageUrl && asset.imageUrl === asset.sourceImageUrl;
            if (needsClone) {
                const fileName = getStorageFileNameFromUrl(asset.sourceImageUrl);
                const destinationPath = `campaigns/${id}/assets/${Date.now()}_${index}_${fileName}`;
                imageUrl = await Storage.copyAsync({ sourceUrl: asset.sourceImageUrl, destinationPath });
            }
            result.push({ ...asset, imageUrl });
        }
        return result;
    };

    const onSubmit = async (values, { setSubmitting }) => {
        let assetsWithImages;
        try {
            assetsWithImages = await cloneAssetImages(values.assets || []);
        } catch (error) {
            dispatch(notifyError(error.message || "Error cloning the campaign images."));
            setSubmitting(false);
            return;
        }

        Campaigns.update({
            payload: buildCampaignPayload(
                { ...values, assets: assetsWithImages },
                { id: Number(id), status: campaign.status, includeAssets: true }
            ),
            onSuccess: () => {
                setSubmitting(false);
                setCloneState(null);
                dispatch(notifySuccess("Campaign updated successfully!"));
                history.push(`/campaigns/${id}`);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                setSubmitting(false);
            },
        });
    };

    return (
        <Page className="campaign-form-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                campaign && (
                    <>
                        <div className="form-head">
                            <div className="item back">
                                <Button secondary link small onClick={onCancel}>
                                    <Icon name="arrow" size="sm" color="secondary" />
                                    <span>Back</span>
                                </Button>
                            </div>
                            <div className="item form-status">
                                <Status id={campaign.status} withIcon />
                            </div>
                            <div className="item title">
                                <h2>{isCloneTarget ? "Clone campaign — assets" : "Edit campaign"}</h2>
                            </div>
                        </div>

                        <div className="campaign-form">
                            {isCloneTarget && (
                                <p className="campaign-form__hint">
                                    These assets were cloned from the original campaign. Review them and save to copy the images and finish
                                    the clone.
                                </p>
                            )}
                            <Form
                                enableReinitialize
                                initialValues={initialValues}
                                validationSchema={editCampaignSchema}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                            >
                                <CampaignBasicFields />
                                <AssetsSection campaignId={id} Storage={Storage} />
                            </Form>
                        </div>
                    </>
                )
            )}
        </Page>
    );
}

export default EditCampaign;
